/**
 * WeekappLogo Component
 *
 * Профессиональный логотип для мобильного приложения учёта рабочего времени
 * Поддерживает 3 варианта отображения и 4 цветовые схемы
 */

import { useMemo } from 'react';

// Цветовые схемы логотипа
export type ColorScheme = 'blue' | 'purple' | 'green' | 'orange';

// Варианты отображения
export type LogoVariant = 'full' | 'icon' | 'text';

interface WeekappLogoProps {
  variant?: LogoVariant;
  colorScheme?: ColorScheme;
  className?: string;
  width?: number;
  height?: number;
}

const colors = {
  blue: {
    primary: '#2563eb',
    secondary: '#3b82f6',
    accent: '#60a5fa',
    dark: '#1e40af',
  },
  purple: {
    primary: '#7c3aed',
    secondary: '#8b5cf6',
    accent: '#a78bfa',
    dark: '#5b21b6',
  },
  green: {
    primary: '#059669',
    secondary: '#10b981',
    accent: '#34d399',
    dark: '#047857',
  },
  orange: {
    primary: '#ea580c',
    secondary: '#f97316',
    accent: '#fb923c',
    dark: '#c2410c',
  },
};

/**
 * Полный логотип с иконкой и текстом
 * Используется на страницах авторизации, splash screen
 */
function FullLogo({ colorScheme = 'blue' }: { colorScheme: ColorScheme }) {
  const currentColors = colors[colorScheme];

  return (
    <svg width="400" height="120" viewBox="0 0 400 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`gradient-${colorScheme}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={currentColors.primary} />
          <stop offset="100%" stopColor={currentColors.secondary} />
        </linearGradient>
        <filter id="shadow">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.15" />
        </filter>
      </defs>

      {/* Иконка - календарь с галочкой */}
      <g transform="translate(20, 20)">
        {/* Внешний контур календаря */}
        <rect
          x="0"
          y="15"
          width="70"
          height="65"
          rx="12"
          fill={`url(#gradient-${colorScheme})`}
          filter="url(#shadow)"
        />

        {/* Верхняя панель календаря */}
        <rect
          x="0"
          y="15"
          width="70"
          height="20"
          rx="12"
          fill={currentColors.dark}
        />
        <rect
          x="0"
          y="25"
          width="70"
          height="10"
          fill={currentColors.dark}
        />

        {/* Крепления календаря */}
        <rect x="15" y="10" width="6" height="10" rx="3" fill={currentColors.dark} />
        <rect x="49" y="10" width="6" height="10" rx="3" fill={currentColors.dark} />

        {/* Дни недели - точки */}
        <circle cx="17" cy="45" r="3" fill="white" opacity="0.9" />
        <circle cx="28" cy="45" r="3" fill="white" opacity="0.9" />
        <circle cx="39" cy="45" r="3" fill="white" opacity="0.9" />
        <circle cx="50" cy="45" r="3" fill="white" opacity="0.9" />
        <circle cx="17" cy="57" r="3" fill="white" opacity="0.9" />
        <circle cx="28" cy="57" r="3" fill="white" opacity="0.9" />
        <circle cx="39" cy="57" r="3" fill="white" opacity="0.9" />

        {/* Активный день - большая галочка */}
        <circle cx="50" cy="57" r="8" fill="white" />
        <path
          d="M 46 57 L 49 60 L 54 54"
          stroke={currentColors.primary}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />

        {/* Часы на фоне */}
        <g opacity="0.3">
          <circle cx="17" cy="69" r="2.5" fill="white" />
          <line x1="17" y1="69" x2="17" y2="67" stroke="white" strokeWidth="1" strokeLinecap="round" />
          <line x1="17" y1="69" x2="19" y2="69" stroke="white" strokeWidth="1" strokeLinecap="round" />
        </g>
      </g>

      {/* Текст WEEKAPP */}
      <g transform="translate(110, 60)">
        <text
          x="0"
          y="0"
          fontFamily="Arial, sans-serif"
          fontWeight="800"
          fontSize="42"
          fill={currentColors.dark}
          letterSpacing="-1"
        >
          Week
        </text>
        <text
          x="110"
          y="0"
          fontFamily="Arial, sans-serif"
          fontWeight="300"
          fontSize="42"
          fill={currentColors.primary}
        >
          app
        </text>
      </g>

      {/* Тагline */}
      <text
        x="110"
        y="85"
        fontFamily="Arial, sans-serif"
        fontWeight="400"
        fontSize="14"
        fill="#64748b"
        letterSpacing="2"
      >
        TIME CONTROL OFFICE
      </text>
    </svg>
  );
}

/**
 * Только иконка (для приложения)
 * Используется в sidebar, navbar, favicon
 */
function IconOnly({ colorScheme = 'blue' }: { colorScheme: ColorScheme }) {
  const currentColors = colors[colorScheme];

  return (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`icon-gradient-${colorScheme}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={currentColors.primary} />
          <stop offset="100%" stopColor={currentColors.secondary} />
        </linearGradient>
        <filter id="icon-shadow">
          <feDropShadow dx="0" dy="6" stdDeviation="8" floodOpacity="0.2" />
        </filter>
      </defs>

      {/* Фон с закругленными углами */}
      <rect
        x="10"
        y="10"
        width="100"
        height="100"
        rx="22"
        fill="white"
        filter="url(#icon-shadow)"
      />

      {/* Календарь */}
      <g transform="translate(25, 25)">
        <rect
          x="0"
          y="15"
          width="70"
          height="65"
          rx="12"
          fill={`url(#icon-gradient-${colorScheme})`}
        />

        <rect
          x="0"
          y="15"
          width="70"
          height="20"
          rx="12"
          fill={currentColors.dark}
        />
        <rect
          x="0"
          y="25"
          width="70"
          height="10"
          fill={currentColors.dark}
        />

        <rect x="15" y="10" width="6" height="10" rx="3" fill={currentColors.dark} />
        <rect x="49" y="10" width="6" height="10" rx="3" fill={currentColors.dark} />

        <circle cx="17" cy="45" r="3" fill="white" opacity="0.9" />
        <circle cx="28" cy="45" r="3" fill="white" opacity="0.9" />
        <circle cx="39" cy="45" r="3" fill="white" opacity="0.9" />
        <circle cx="50" cy="45" r="3" fill="white" opacity="0.9" />
        <circle cx="17" cy="57" r="3" fill="white" opacity="0.9" />
        <circle cx="28" cy="57" r="3" fill="white" opacity="0.9" />
        <circle cx="39" cy="57" r="3" fill="white" opacity="0.9" />

        <circle cx="50" cy="57" r="8" fill="white" />
        <path
          d="M 46 57 L 49 60 L 54 54"
          stroke={currentColors.primary}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />

        <g opacity="0.3">
          <circle cx="17" cy="69" r="2.5" fill="white" />
          <line x1="17" y1="69" x2="17" y2="67" stroke="white" strokeWidth="1" strokeLinecap="round" />
          <line x1="17" y1="69" x2="19" y2="69" stroke="white" strokeWidth="1" strokeLinecap="round" />
        </g>
      </g>
    </svg>
  );
}

/**
 * Минимальный - только название
 * Используется в header, компактных местах
 */
function TextOnly({ colorScheme = 'blue' }: { colorScheme: ColorScheme }) {
  const currentColors = colors[colorScheme];

  return (
    <svg width="300" height="80" viewBox="0 0 300 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      <text
        x="0"
        y="50"
        fontFamily="Arial, sans-serif"
        fontWeight="800"
        fontSize="48"
        fill={currentColors.dark}
        letterSpacing="-1"
      >
        Week
      </text>
      <text
        x="125"
        y="50"
        fontFamily="Arial, sans-serif"
        fontWeight="300"
        fontSize="48"
        fill={currentColors.primary}
      >
        app
      </text>
    </svg>
  );
}

/**
 * Главный экспортируемый компонент
 *
 * @example
 * // Полный логотип на странице авторизации
 * <WeekappLogo variant="full" colorScheme="blue" />
 *
 * @example
 * // Иконка в sidebar
 * <WeekappLogo variant="icon" colorScheme="blue" width={48} />
 *
 * @example
 * // Только текст в header
 * <WeekappLogo variant="text" colorScheme="purple" />
 */
export function WeekappLogo({
  variant = 'full',
  colorScheme = 'blue',
  className = '',
  width,
  height
}: WeekappLogoProps) {
  const LogoComponent = useMemo(() => {
    switch (variant) {
      case 'icon':
        return IconOnly;
      case 'text':
        return TextOnly;
      case 'full':
      default:
        return FullLogo;
    }
  }, [variant]);

  const style = useMemo(() => {
    const baseStyle: React.CSSProperties = {};
    if (width) baseStyle.width = width;
    if (height) baseStyle.height = height;
    return baseStyle;
  }, [width, height]);

  return (
    <div className={className} style={style}>
      <LogoComponent colorScheme={colorScheme} />
    </div>
  );
}
